import { gql, useQuery } from "@apollo/client";
import { DateTime } from "luxon";
import { useState } from "react";
import { Bar } from "react-chartjs-2";
import { StringParam, useQueryParam, withDefault } from "use-query-params";

import {
  Button,
  createStyles,
  Dialog,
  DialogTitle,
  Grid,
  makeStyles,
  Paper,
  useTheme,
  DialogContent,
  DialogActions
} from "@material-ui/core";
import DownloadIcon from "@material-ui/icons/CloudDownload";
import { Alert } from "@material-ui/lab";

import ErrorCard from "src/components/ErrorCard";
import LoadingIndicator from "src/components/LoadingIndicator";
import { useSubaccount } from "src/components/SubaccountProvider";
import DateRangePicker from "src/components/form/DateRangePicker";
import LinearProgressWithLabel from "src/components/stats/LinearProgressWithLabel";
import NumberStat from "src/components/stats/NumberStat";
import { campaignExport } from "src/graphql/generated/campaignExport";
import {
  getCampaign_campaign,
  getCampaign_campaign_questions
} from "src/graphql/generated/getCampaign";
import {
  getCampaignStats_campaign_filteredStats_callStatuses,
  getCampaignStats_campaign_filteredStats_surveyResults,
  getCampaignStats_campaign_filteredStats_callDurationsWithStatuses,
  getCampaignStats
} from "src/graphql/generated/getCampaignStats";
import { getExportJob } from "src/graphql/generated/getExportJob";
import {
  ApprovalRequirement,
  CallStatus,
  JobStatus,
  QuestionType,
  Role
} from "src/graphql/generated/globalTypes";
import palette from "src/styles/palette";
import formatDuration from "src/util/formatDuration";
import { hasRole } from "src/util/roles";
import useMutation from "src/util/useMutation";

import { QUESTION_TYPES_WITH_ANSWERS } from "./Questions/Question";

const GET_CAMPAIGN_STATS = gql`
  query getCampaignStats($id: ID!, $startDate: String!, $endDate: String!) {
    campaign(id: $id) {
      id
      stats {
        callProgress {
          pass
          completed
          required
        }
        requiredApprovals
        completedApprovals
        skippedContacts
        callsLastFiveMinutes
        callsLastHalfHour
        callsToday
        waitTimeAverageLastFiveMinutes
        waitTimeAverageLastHalfHour
        waitTimeAverageToday
      }
      filteredStats(startDate: $startDate, endDate: $endDate) {
        calls
        callsAverageDuration
        callsUnderSixSeconds
        callDurationsWithStatuses {
          status
          rangeMin
          rangeMax
          count
        }
        callStatuses {
          status
          count
        }
        surveyResults {
          questionKey
          answerValue
          count
        }
      }
    }
  }
`;

const GET_EXPORT_JOB = gql`
  query getExportJob($id: ID!) {
    job(id: $id) {
      id
      type
      createTime
      startTime
      progress
      status
      result
      errorCode
      errorMessage
      errorDetails
    }
  }
`;

const useStyles = makeStyles(theme =>
  createStyles({
    paper: {
      padding: theme.spacing(3),
      textAlign: "center",
      color: theme.palette.text.secondary,
      maxWidth: "100%"
    },
    pieWrapper: {
      height: "200px",
      width: "100%"
    },
    surveyQuestionWrapper: {
      height: "300px",
      width: "100%"
    },
    singleNumberWrapper: {
      width: "100%",
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around"
    },
    progressWrapper: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      minHeight: "256px"
    },
    noClickerProgress: {
      minHeight: "135px"
    },
    passesWrapper: {
      display: "flex",
      flexDirection: "column",
      rowGap: theme.spacing(3),
      marginBottom: theme.spacing(3)
    },
    actionsWrapper: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "flex-end",
      marginBottom: theme.spacing(3)
    },
    dialogBody: {
      width: "50vw",
      maxWidth: "600px",
      minWidth: "300px"
    },
    numberStatRow: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-around",
      flexWrap: "wrap"
    },
    averageCallDurationWrapper: {
      minHeight: "256px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    },
    minHeightChartWrapper: {
      minHeight: "202px",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center"
    },
    aggegrateHeading: {
      marginTop: theme.spacing(3)
    },
    dateRangeWrapper: {
      paddingTop: theme.spacing(7),
      marginRight: theme.spacing(2)
    }
  })
);

const STATUS_COLORS: { [status in CallStatus]: string } = {
  [CallStatus.ANSWERING_MACHINE]: palette.darkBlue,
  [CallStatus.ANSWERING_MACHINE_IN_PROGRESS]: palette.darkBlue,
  [CallStatus.VOICEMAIL_DROP]: palette.darkBlue,
  [CallStatus.BUSY]: palette.darkGray,
  [CallStatus.COMPLETED]: palette.darkGreen,
  [CallStatus.DECLINED]: palette.midGray,
  [CallStatus.HUNGUP]: palette.midGray,
  [CallStatus.FAILED]: palette.red,
  [CallStatus.NO_ANSWER]: palette.navy,
  [CallStatus.IN_PROGRESS]: palette.green,
  [CallStatus.CONNECTED]: palette.green,
  [CallStatus.ERROR]: palette.red
};

const STATUS_NAMES: { [status in CallStatus]: string } = {
  [CallStatus.ANSWERING_MACHINE]: "Answering Machine",
  [CallStatus.ANSWERING_MACHINE_IN_PROGRESS]: "Leaving Voicemail",
  [CallStatus.VOICEMAIL_DROP]: "Left Voicemail",
  [CallStatus.BUSY]: "Busy",
  [CallStatus.COMPLETED]: "Completed",
  [CallStatus.DECLINED]: "Declined",
  [CallStatus.HUNGUP]: "Hungup",
  [CallStatus.FAILED]: "Failed",
  [CallStatus.NO_ANSWER]: "No Answer",
  [CallStatus.IN_PROGRESS]: "In Progress",
  [CallStatus.CONNECTED]: "Connected",
  [CallStatus.ERROR]: "Error"
};

function percentageString(numerator: number, denominator: number) {
  // Computes a percentage, defensively handling null/undefined/0 in the
  // numerator or denominator
  const ratio = !numerator || !denominator ? 0 : numerator / denominator;

  return `${Math.round(ratio * 100)}%`;
}

function CallStatuses({
  callStatuses
}: {
  callStatuses: getCampaignStats_campaign_filteredStats_callStatuses[];
}): JSX.Element {
  const classes = useStyles();

  const labels: string[] = [];
  const statusData: number[] = [];
  const colors: string[] = [];

  callStatuses.forEach(({ status, count }) => {
    labels.push(STATUS_NAMES[status]);
    statusData.push(count);
    colors.push(STATUS_COLORS[status]);
  });

  const data = {
    labels,
    datasets: [
      {
        label: "Call Status",
        data: statusData,
        backgroundColor: colors
      }
    ]
  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    }
  };

  return (
    <div className={classes.pieWrapper}>
      <Bar data={data} options={options} />
    </div>
  );
}

function SurveyResultAnswerChart({
  question,
  results: unsortedResults
}: {
  question: getCampaign_campaign_questions;
  results: getCampaignStats_campaign_filteredStats_surveyResults[];
}): JSX.Element {
  const theme = useTheme();
  const classes = useStyles();

  // sort the answers by the order they are defined in the campaign,
  // with deleted answers at the end
  const answerIndexes: { [answerValue: string]: number } = {};
  question.answers.forEach(
    (answer, idx) => (answerIndexes[answer.value] = idx)
  );

  const results = [...unsortedResults].sort((a, b) => {
    const aIndex = answerIndexes[a.answerValue] ?? Number.MAX_SAFE_INTEGER;
    const bIndex = answerIndexes[b.answerValue] ?? Number.MAX_SAFE_INTEGER;
    return aIndex - bIndex;
  });

  // construct the data for the chart
  const totalResponses = results.reduce((acc, val) => acc + val.count, 0);

  const includePercentage = [
    QuestionType.SINGLE_CHOICE,
    QuestionType.DISPOSITION,
    QuestionType.POLLING_PLACE_LOOKUP
  ].includes(question.questionType);

  const data = {
    labels: results.map(({ answerValue, count }) => {
      const answerLabel =
        answerValue.length > 23
          ? `${answerValue.slice(0, 20)}...`
          : answerValue;

      if (includePercentage) {
        const percentageLabel = percentageString(count, totalResponses);
        return `${answerLabel} (${percentageLabel})`;
      }

      return answerLabel;
    }),
    datasets: [
      {
        label: "# of Responses",
        data: results.map(r => r.count),
        backgroundColor: theme.palette.primary.main
      }
    ]
  };

  const options = {
    scales: {
      y: {
        ticks: {
          beginAtZero: true,
          precision: 0 // integers only
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    }
  };
  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <h3>{question.name}</h3>
        {results.length ? (
          <div className={classes.surveyQuestionWrapper}>
            <Bar data={data} options={options} />
          </div>
        ) : (
          <p>No Answers In This Time Period</p>
        )}
      </Paper>
    </Grid>
  );
}

function SurveyResultSingleNumber({
  question,
  value
}: {
  question: getCampaign_campaign_questions;
  value: number;
}) {
  const classes = useStyles();

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <h3>{question.name}</h3>
        <div className={classes.singleNumberWrapper}>
          <NumberStat value={value}>Text Messages Sent</NumberStat>
        </div>
      </Paper>
    </Grid>
  );
}

function SurveyResults({
  campaign,
  surveyResults
}: {
  campaign: getCampaign_campaign;
  surveyResults: getCampaignStats_campaign_filteredStats_surveyResults[];
}) {
  // process the survey results into a map
  const surveyResultsMap: {
    [
      questionKey: string
    ]: getCampaignStats_campaign_filteredStats_surveyResults[];
  } = {};
  surveyResults.forEach(r => {
    if (!surveyResultsMap[r.questionKey]) {
      surveyResultsMap[r.questionKey] = [];
    }
    surveyResultsMap[r.questionKey].push(r);
  });

  return (
    <>
      {campaign.questions.map(question => {
        if (QUESTION_TYPES_WITH_ANSWERS.includes(question.questionType)) {
          return (
            <SurveyResultAnswerChart
              key={question.key}
              question={question}
              results={surveyResultsMap[question.key] || []}
            />
          );
        } else if (question.questionType === QuestionType.SEND_MESSAGE) {
          return (
            <SurveyResultSingleNumber
              key={question.key}
              question={question}
              value={surveyResultsMap[question.key]?.[0]?.count ?? 0}
            />
          );
        }
      })}
    </>
  );
}

// backend reports call duration in 6-second buckets
const CALL_DURATION_BUCKET_WIDTH = 6;
function CallDurations({
  callDurations
}: {
  callDurations: getCampaignStats_campaign_filteredStats_callDurationsWithStatuses[];
}) {
  const classes = useStyles();

  if (callDurations.length === 0) {
    return <p>No Calls In This Time Period</p>;
  }

  const statuses: CallStatus[] = Array.from(
    new Set(callDurations.map(duration => duration.status))
  );

  const maxBucket = callDurations.map(duration => duration.rangeMin).sort()[
    callDurations.length - 1
  ];

  const durationBucketStarts: number[] = [];
  for (
    let bucketStart = 0;
    bucketStart <= maxBucket;
    bucketStart += CALL_DURATION_BUCKET_WIDTH
  ) {
    durationBucketStarts.push(bucketStart);
  }

  const labels = durationBucketStarts.map(
    bucketStart =>
      `${formatDuration(bucketStart)}-${formatDuration(
        bucketStart + CALL_DURATION_BUCKET_WIDTH
      )}`
  );

  const datasets = statuses.map(status => {
    const callDurationsByRangeMin: { [rangeMin: number]: number } = {};
    callDurations
      .filter(duration => duration.status === status)
      .forEach(({ rangeMin, count }) => {
        callDurationsByRangeMin[rangeMin] = count;
      });

    return {
      data: durationBucketStarts.map(
        bucketStart => callDurationsByRangeMin[bucketStart] || 0
      ),
      label: STATUS_NAMES[status],
      backgroundColor: STATUS_COLORS[status]
    };
  });

  const data = {
    labels,
    datasets // TODO: make sure statuses are shown in a reasonable order?
  };

  const options = {
    scales: {
      y: {
        stacked: true,
        title: {
          display: true,
          text: "# of calls"
        },
        ticks: {
          beginAtZero: true,
          precision: 0 // integers only
        }
      },
      x: {
        stacked: true,
        ticks: {
          callback: (val: string | number) => {
            const label = data.labels[val as number];
            return label.length > 30 ? `${label.slice(0, 27)}...` : label;
          }
        }
      }
    },
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true
      }
    }
  };

  return (
    <div className={classes.pieWrapper}>
      <Bar data={data} options={options} />
    </div>
  );
}

function ExportProgressDialog({
  open,
  onClose,
  jobId
}: {
  open: boolean;
  onClose: () => void;
  jobId: string | null;
}): JSX.Element {
  const classes = useStyles();

  const { loading, error, data } = useQuery<getExportJob>(GET_EXPORT_JOB, {
    variables: {
      id: jobId
    },
    skip: !jobId,
    pollInterval: 3000
  });

  const isJobDone =
    jobId &&
    !loading &&
    (data?.job?.status === JobStatus.DONE ||
      data?.job?.status === JobStatus.FAILED);

  function handleDialogImplicitClose() {
    // dialog can only be closed by click-away or escape when the job is finished
    // (otherwise we require an explicit "close" click because we discard the
    // job ID and you can't get back to the dialog)
    if (isJobDone) {
      onClose();
    }
  }

  function renderJobStatus() {
    if (error) {
      return <ErrorCard errors={[error]} />;
    }

    if (!jobId || loading || data?.job?.progress === 0) {
      return <LoadingIndicator message="Starting Job..." />;
    }

    if (data?.job?.status === JobStatus.FAILED) {
      return (
        <Alert severity="error">
          Campaign export failed: {data?.job.errorMessage}
        </Alert>
      );
    }

    if (data?.job?.status === JobStatus.DONE) {
      const { s3SignedURL }: { s3SignedURL: string } = JSON.parse(
        data.job.result!
      );

      return (
        <div>
          <Button
            href={s3SignedURL}
            target="_blank"
            startIcon={<DownloadIcon />}
            variant="contained"
            color="primary"
          >
            Download CSV
          </Button>
        </div>
      );
    }

    const progress = data?.job?.progress || 0;
    return (
      <LoadingIndicator
        message={`Exporting (${(progress * 100).toFixed(1)}%)...`}
        progress={progress}
      />
    );
  }

  return (
    <Dialog
      open={open}
      onClose={handleDialogImplicitClose}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle id="alert-dialog-title">Export Campaign</DialogTitle>
      <DialogContent className={classes.dialogBody}>
        {renderJobStatus()}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          color={isJobDone ? "default" : "secondary"}
          variant="contained"
        >
          {isJobDone ? "Close" : "Cancel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function validateStartDate(startDate: string): string {
  const dt = DateTime.fromISO(startDate);
  if (!dt.isValid) {
    return DateTime.now().startOf("month").toISODate();
  }
  return dt.toISODate();
}

function validateEndDate(endDate: string, validatedStartDate: string): string {
  const dt = DateTime.fromISO(endDate);
  if (!dt.isValid || dt < DateTime.fromISO(validatedStartDate)) {
    return DateTime.now().endOf("month").toISODate();
  }
  return dt.toISODate();
}

function CampaignStats({ campaign }: { campaign: getCampaign_campaign }) {
  const timezone = campaign.callingHours.timezone;
  const now = DateTime.now().setZone(timezone).toISODate();
  const campaignStart = DateTime.fromJSDate(new Date(campaign.created_at))
    .setZone(timezone)
    .toISODate();

  const [startDate, setStartDate] = useQueryParam(
    "start_date",
    withDefault(StringParam, campaignStart)
  );
  const [endDate, setEndDate] = useQueryParam(
    "end_date",
    withDefault(StringParam, now)
  );

  const { loading, error, data } = useQuery<getCampaignStats>(
    GET_CAMPAIGN_STATS,
    {
      variables: {
        id: campaign.id,
        startDate,
        endDate
      },
      pollInterval: 30 * 1000
    }
  );

  const classes = useStyles();

  const validatedStartDate = validateStartDate(startDate);
  const validatedEndDate = validateEndDate(endDate, validatedStartDate);

  if (loading) {
    return <LoadingIndicator />;
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  if (!data?.campaign?.stats || !data?.campaign?.filteredStats) {
    return <div />;
  }

  const {
    callProgress,
    skippedContacts,
    completedApprovals,
    requiredApprovals,
    callsLastFiveMinutes,
    callsLastHalfHour,
    callsToday,
    waitTimeAverageLastFiveMinutes,
    waitTimeAverageLastHalfHour,
    waitTimeAverageToday
  } = data.campaign.stats;

  const {
    calls,
    callStatuses,
    surveyResults,
    callDurationsWithStatuses,
    callsAverageDuration
  } = data.campaign.filteredStats;

  const callsInProgress =
    callStatuses.find(s => s.status === "IN_PROGRESS")?.count ?? 0;

  const totalApprovals = requiredApprovals + completedApprovals;
  const progressWrapperClasses = [classes.progressWrapper];
  if (campaign.approval_requirement === ApprovalRequirement.NONE) {
    progressWrapperClasses.push(classes.noClickerProgress);
  }

  return (
    <Grid container spacing={3}>
      {/* Progress and call statuses */}
      <Grid item xs={12} md={6}>
        <Paper className={classes.paper}>
          <div className={progressWrapperClasses.join(" ")}>
            <div>
              <h3>Call Progress</h3>
              <div className={classes.passesWrapper}>
                {(callProgress || []).map(({ pass, completed, required }) => (
                  <div>
                    Pass {pass}: Called {completed.toLocaleString()} of{" "}
                    {(completed + required).toLocaleString()} contacts
                    <LinearProgressWithLabel
                      value={(completed / (completed + required)) * 100}
                    />
                  </div>
                ))}
              </div>
              {skippedContacts > 0 ? (
                <div>
                  Skipped {skippedContacts.toLocaleString()} contact
                  {skippedContacts > 1 ? "s" : ""}.
                </div>
              ) : null}
            </div>

            <div>
              {totalApprovals > 0 && (
                <>
                  <h3>Click Progress</h3>
                  <div>
                    Approved {completedApprovals.toLocaleString()} of{" "}
                    {totalApprovals.toLocaleString()} contacts
                    <LinearProgressWithLabel
                      value={(completedApprovals / totalApprovals) * 100}
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper className={classes.paper}>
          <NumberStat value={callsInProgress}>Calls In Progress</NumberStat>
        </Paper>
      </Grid>

      {/* Calls in progress and calls made */}
      <Grid item xs={12} md={12}>
        <Paper className={classes.paper}>
          <div className={classes.numberStatRow}>
            <NumberStat value={callsLastFiveMinutes}>
              Calls, Past 5 Minutes
            </NumberStat>
            <NumberStat value={callsLastHalfHour}>
              Calls, Past 30 Minutes
            </NumberStat>
            <NumberStat value={callsToday}>Calls, Today</NumberStat>
          </div>
        </Paper>
      </Grid>

      {/* Wait Times */}
      <Grid item xs={12} md={12}>
        <Paper className={classes.paper}>
          <h3>Average Wait Time</h3>
          <div className={classes.numberStatRow}>
            <NumberStat
              format="duration"
              value={waitTimeAverageLastFiveMinutes}
            >
              Past 5 Minutes
            </NumberStat>
            <NumberStat format="duration" value={waitTimeAverageLastHalfHour}>
              Past 30 Minutes
            </NumberStat>
            <NumberStat format="duration" value={waitTimeAverageToday}>
              Today
            </NumberStat>
          </div>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6} className={classes.aggegrateHeading}>
        <h2>Campaign Results</h2>
      </Grid>
      <Grid container xs={12} md={6} justifyContent="flex-end">
        <div className={classes.dateRangeWrapper}>
          <div>Show Results From</div>
          <DateRangePicker
            timezone={timezone}
            startDate={validatedStartDate}
            endDate={validatedEndDate}
            shortcuts={[
              {
                title: "All Results",
                startDate: campaignStart,
                endDate: now
              }
            ]}
            onChange={(newStartDate, newEndDate) => {
              setStartDate(newStartDate);
              setEndDate(newEndDate);
            }}
          />
        </div>
      </Grid>

      <Grid item xs={12} md={9}>
        <Paper className={classes.paper}>
          <div className={classes.numberStatRow}>
            <NumberStat value={calls}>Calls</NumberStat>
            <NumberStat format="duration" value={callsAverageDuration}>
              Average Call Duration
            </NumberStat>
          </div>
        </Paper>
      </Grid>

      <Grid item xs={12} md={12}>
        <Paper className={classes.paper}>
          <h3>Call Status</h3>
          <div className={classes.minHeightChartWrapper}>
            {callStatuses.length > 0 ? (
              <CallStatuses callStatuses={callStatuses} />
            ) : (
              <p>No Calls In This Time Period</p>
            )}
          </div>
        </Paper>
      </Grid>

      {/* Call Durations */}
      <Grid item xs={12} md={12}>
        <Paper className={classes.paper}>
          <h3>Call Durations</h3>
          <div className={classes.minHeightChartWrapper}>
            <CallDurations callDurations={callDurationsWithStatuses} />
          </div>
        </Paper>
      </Grid>

      {/* Survey Results */}
      <SurveyResults campaign={campaign} surveyResults={surveyResults} />
    </Grid>
  );
}

function CampaignExport({ campaign }: { campaign: getCampaign_campaign }) {
  const [startExportJob] = useMutation<campaignExport>(
    gql`
      mutation campaignExport($id: ID!) {
        campaignExport(id: $id) {
          id
        }
      }
    `,
    {
      onErrorSnackbarMessage: "Could not export campaign data"
    }
  );

  const classes = useStyles();
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [exportJobId, setExportJobId] = useState<string | null>(null);

  async function handleExport() {
    setExportModalOpen(true);

    const exportJob = await startExportJob({
      variables: { id: campaign.id }
    });
    if (exportJob.errors) {
      setExportModalOpen(false);
      return;
    }

    setExportJobId(exportJob.data!.campaignExport.id);
  }

  return (
    <>
      <div className={classes.actionsWrapper}>
        <Button onClick={handleExport} variant="contained" color="primary">
          Export CSV
        </Button>
      </div>
      <ExportProgressDialog
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        jobId={exportJobId}
      />
    </>
  );
}

export default function Results({
  campaign
}: {
  campaign: getCampaign_campaign;
}): JSX.Element {
  const { membership, subaccount } = useSubaccount()!;
  const exportsRestricted =
    subaccount.settings.restrictExports && !hasRole(membership, Role.OWNER);

  return (
    <>
      <h2>Campaign Progress</h2>

      {!exportsRestricted && <CampaignExport campaign={campaign} />}
      <CampaignStats campaign={campaign} />
    </>
  );
}
